import { UseQueryResult } from '@tanstack/react-query';
import { format, isDate, isToday, isYesterday, parseISO } from 'date-fns';
import { i18n } from 'i18next';
import { QueryFactory } from 'services/api';
import {
  AbasErpDataTableItem,
  QueryResponseField,
} from 'services/api/api-client.types';

type PriorityKey = 'value' | 'text' | undefined;

type PriorityObj = {
  [key: string]: 'value' | 'text' | 'both';
};

export type ErrObj = {
  response: {
    type: string;
  };
};

export function getFieldValue(
  value: QueryResponseField,
  priority?: PriorityKey,
) {
  if (!value) return '';

  if (priority && value[priority] !== undefined) {
    return value[priority];
  }

  return value.text ?? value.value;
}

export function convertAbasTable(
  data: AbasErpDataTableItem[] | undefined | null,
  options?: {
    idField?: string;
    priorities?: PriorityObj;
  },
) {
  return data?.map((row) => {
    const result: Record<string, string> = {};
    if (options?.idField) result['_id'] = row.fields[options.idField].value;

    Object.keys(row.fields).forEach((fieldKey) => {
      const priority = options?.priorities?.[fieldKey];
      if (priority === 'both') {
        result[fieldKey] = getFieldValue(row.fields[fieldKey], 'text');
        result[fieldKey + 'Value'] = getFieldValue(
          row.fields[fieldKey],
          'value',
        );
      } else result[fieldKey] = getFieldValue(row.fields[fieldKey], priority);
    });
    return result;
  });
}

export function getQueryHeadField(
  query: UseQueryResult<QueryFactory.AbasExecuteResponse, unknown>,
  field: string,
) {
  const headData = query.data?.content.data.head.fields ?? {};
  return getFieldValue(headData[field]);
}

export const sortDate = (firstValue: any, secondValue: any) => {
  try {
    const firstDate = isDate(firstValue) ? firstValue : parseISO(firstValue);
    const secondDate = isDate(secondValue)
      ? secondValue
      : parseISO(secondValue);

    return firstDate.getTime() - secondDate.getTime();
  } catch (e) {
    return firstValue - secondValue;
  }
};

export const formatDate = (
  date: number | string | Date,
  isHoursNeeded: boolean = false,
) => {
  if (!date) {
    return null;
  }

  try {
    return format(new Date(date), `PP ${isHoursNeeded ? 'p' : ''}`);
  } catch (e) {
    return null;
  }
};

export const formatDetailedDate = (
  date: number | string | Date,
  i18n: i18n,
  withTimePart: boolean = true,
): string | null => {
  if (date == null || date === '') {
    return i18n.t('never');
  }

  let d: Date;
  try {
    d = new Date(date);
    if (isNaN(d.getTime())) {
      return null;
    }
  } catch {
    return null;
  }

  const timePart = withTimePart ? format(d, 'HH:mm') : '';

  if (isToday(d)) {
    return withTimePart ? `${i18n.t('today')}, ${timePart}` : i18n.t('today');
  }

  if (isYesterday(d)) {
    return withTimePart
      ? `${i18n.t('yesterday')}, ${timePart}`
      : i18n.t('yesterday');
  }

  return withTimePart ? format(d, 'PP p') : format(d, 'PP');
};

export const abasUnavailableUrn = 'urn:mccsoft:abas-is-unavailable';
